import { Injectable } from '@angular/core';
import { __ } from '@shared/functions/object.functions';
import { Subject } from 'rxjs';

const STORAGE_PREFIX: string = 'mp-feed-position:';

/** The page size the feeds load with. */
const FEED_PAGE_SIZE: number = 10;

/** A position expires this long after it was first stored: the feed then starts at the top again. */
const POSITION_MAX_AGE_MS: number = 24 * 60 * 60 * 1000;

/** The stored position of a feed with the time the position chain was started. */
interface StoredPosition {
    index: number;
    createdAt: number;
}

/**
 * The furthest pop the user reached in the For You / Pop Mix feed (`position` of the app's `BasePaginationStream`,
 * persisted per `FeedChannel`). Reopening the feed continues there: the first request fetches enough pops to cover
 * the position (`resumeFetchTarget` of the app), later pages continue with `skip` / `take` after them.
 *
 * The expiry is a hard 24 hour ttl counted from the FIRST store of the chain (not from the last activity):
 * daily usage must not keep a position alive forever, and the feed itself is recomputed daily anyway (due
 * reviews are injected at the top), so a day-old position points into a reshuffled feed.
 */
@Injectable({ providedIn: 'root' })
export class FeedPositionService {

    /** Emits the surface whose feed should scroll back to the top (e.g. the active tab was tapped again). */
    readonly scrollToTopRequested$: Subject<string> = new Subject<string>();

    /** The stored position of a feed (0 when none or when it expired). */
    get(surface: string): number {
        const stored = this.read(surface);

        if (__.IsNullOrUndefined(stored)) {
            return 0;
        }

        if (Date.now() - stored.createdAt > POSITION_MAX_AGE_MS) {
            this.reset(surface);

            return 0;
        }

        return stored.index;
    }

    /**
     * Advances the position (it only moves forward, like `markPositionSeen`). The creation time of the
     * chain is preserved, so the position still expires 24 hours after it was started; an expired chain
     * starts over at the current index.
     */
    markSeen(surface: string, index: number): void {
        if (__.IsNullOrUndefined(index) || index < 0) {
            return;
        }

        try {
            const existing = this.read(surface);
            const isExpired = __.IsNullOrUndefined(existing) || Date.now() - existing.createdAt > POSITION_MAX_AGE_MS;

            const stored: StoredPosition = {
                index: isExpired ? index : Math.max(index, existing.index),
                createdAt: isExpired ? Date.now() : existing.createdAt
            };

            localStorage.setItem(STORAGE_PREFIX + surface, JSON.stringify(stored));
        } catch {
            // Storage may be unavailable
        }
    }

    /** A refreshed feed starts at the beginning again. */
    reset(surface: string): void {
        try {
            localStorage.removeItem(STORAGE_PREFIX + surface);
        } catch {
            // Storage may be unavailable
        }
    }

    /** Asks the feed of the surface to scroll back to the first pop (the stored position stays). */
    requestScrollToTop(surface: string): void {
        this.scrollToTopRequested$.next(surface);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PRIVATE METHODS
    // -----------------------------------------------------------------------------------------------------

    /**
     * Reads the stored position. Older formats (a plain number, or the former `updatedAt` field whose
     * sliding refresh prevented the expiry) are treated as unknown so they are forgotten.
     */
    private read(surface: string): StoredPosition {
        try {
            const raw = localStorage.getItem(STORAGE_PREFIX + surface);

            if (__.IsNullOrUndefinedOrEmpty(raw) || !raw.startsWith('{')) {
                return null;
            }

            const parsed = JSON.parse(raw);
            const index = Number(parsed?.index);
            const createdAt = Number(parsed?.createdAt);

            if (Number.isNaN(index) || index < 0 || Number.isNaN(createdAt)) {
                return null;
            }

            return { index, createdAt };
        } catch {
            return null;
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ PUBLIC STATIC METHODS
    // -----------------------------------------------------------------------------------------------------

    /** How many pops the first request loads to cover the stored position (`(ceil((position + 1) / 10) + 1) * 10`). */
    static resumeFetchTarget(position: number): number {
        return (Math.ceil((position + 1) / FEED_PAGE_SIZE) + 1) * FEED_PAGE_SIZE;
    }
}
